import { create } from 'zustand';

import {
  DEFAULT_CAMERA_ZOOM,
  DEFAULT_WORLD_LAYOUT,
  normalizeCameraZoom,
  normalizeWorldLayout,
  type WorldLayout,
} from '../game/worldLayout';
import type { AvatarMode } from '../types/customAvatar';
import type {
  ActiveAnnotationMenu,
  ActiveWorldExamination,
  GameState,
  InputVector,
  PlayerPosition,
  RemotePlayerPresence,
} from '../types/game';
import type { NpcConfig } from '../types/npc';
import { DEFAULT_SPRITE_COLORS, type AvatarLayerSelection, type SpriteColors } from '../types/user';

export type RailTabId = 'world' | 'social' | 'simulations' | 'history';

const RAIL_COLLAPSED_STORAGE_KEY = 'waysprite:right-rail-collapsed';

const loadPersistedRailCollapsed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistRailCollapsed = (value: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(RAIL_COLLAPSED_STORAGE_KEY, String(value));
  } catch {
    // Ignore storage failures; the in-memory state remains the source of truth.
  }
};

interface GameStoreState {
  gameState: GameState;
  activeNpcId: string | null;
  remoteTaskAssignmentNpcId: string | null;
  proximityNpcId: string | null;
  interactionPrompt: string | null;
  activeExamination: ActiveWorldExamination | null;
  activeAnnotationMenu: ActiveAnnotationMenu | null;
  activeZone: string;
  virtualInput: InputVector;
  playerPosition: PlayerPosition;
  worldLayout: WorldLayout;
  cameraZoom: number;
  isAutoZoom: boolean;
  playerSpriteColors: SpriteColors;
  playerAvatarLayers: AvatarLayerSelection | null;
  playerAvatarMode: AvatarMode;
  playerCustomAvatarId: string | null;
  npcConfigs: Record<string, NpcConfig>;
  remotePlayers: Record<string, RemotePlayerPresence>;
  friendIds: string[];
  activeRailTab: RailTabId;
  isRailCollapsed: boolean;
  setGameState: (value: GameState) => void;
  setActiveNpcId: (value: string | null) => void;
  setRemoteTaskAssignmentNpcId: (value: string | null) => void;
  setProximityNpcId: (value: string | null) => void;
  setInteractionPrompt: (value: string | null) => void;
  setActiveExamination: (value: ActiveWorldExamination | null) => void;
  setActiveAnnotationMenu: (value: ActiveAnnotationMenu | null) => void;
  setActiveZone: (value: string) => void;
  setVirtualInput: (value: InputVector) => void;
  setPlayerPosition: (value: PlayerPosition) => void;
  setWorldLayout: (value: WorldLayout) => void;
  setCameraZoom: (value: number) => void;
  setAutoZoom: (value: boolean) => void;
  setPlayerSpriteColors: (value: SpriteColors) => void;
  setPlayerAvatarLayers: (value: AvatarLayerSelection | null) => void;
  setPlayerAvatarMode: (value: AvatarMode) => void;
  setPlayerCustomAvatarId: (value: string | null) => void;
  setNpcConfigs: (npcs: NpcConfig[]) => void;
  setRemotePlayers: (players: RemotePlayerPresence[]) => void;
  setFriendIds: (friendIds: string[]) => void;
  setActiveRailTab: (tab: RailTabId) => void;
  setRailCollapsed: (value: boolean) => void;
  toggleRailCollapsed: () => void;
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  gameState: 'loading',
  activeNpcId: null,
  remoteTaskAssignmentNpcId: null,
  proximityNpcId: null,
  interactionPrompt: null,
  activeExamination: null,
  activeAnnotationMenu: null,
  activeZone: 'village',
  virtualInput: { x: 0, y: 0 },
  playerPosition: { x: 0, y: 0 },
  worldLayout: { ...DEFAULT_WORLD_LAYOUT },
  cameraZoom: DEFAULT_CAMERA_ZOOM,
  isAutoZoom: true,
  playerSpriteColors: DEFAULT_SPRITE_COLORS,
  playerAvatarLayers: null,
  playerAvatarMode: 'layers',
  playerCustomAvatarId: null,
  npcConfigs: {},
  remotePlayers: {},
  friendIds: [],
  activeRailTab: 'world',
  isRailCollapsed: loadPersistedRailCollapsed(),
  setGameState: (value) => set(() => ({ gameState: value })),
  setActiveNpcId: (value) => set(() => ({ activeNpcId: value })),
  setRemoteTaskAssignmentNpcId: (value) => set(() => ({ remoteTaskAssignmentNpcId: value })),
  setProximityNpcId: (value) => set(() => ({ proximityNpcId: value })),
  setInteractionPrompt: (value) => set(() => ({ interactionPrompt: value })),
  setActiveExamination: (value) => set(() => ({ activeExamination: value })),
  setActiveAnnotationMenu: (value) => set(() => ({ activeAnnotationMenu: value })),
  setActiveZone: (value) => set(() => ({ activeZone: value })),
  setVirtualInput: (value) => set(() => ({ virtualInput: value })),
  setPlayerPosition: (value) => set(() => ({ playerPosition: value })),
  setWorldLayout: (value) => set(() => ({ worldLayout: normalizeWorldLayout(value) })),
  setCameraZoom: (value) => set(() => ({ cameraZoom: normalizeCameraZoom(value) })),
  setAutoZoom: (isAutoZoom) => set(() => ({ isAutoZoom })),
  setPlayerSpriteColors: (value) => set(() => ({ playerSpriteColors: value })),
  setPlayerAvatarLayers: (value) => set(() => ({ playerAvatarLayers: value })),
  setPlayerAvatarMode: (value) => set(() => ({ playerAvatarMode: value })),
  setPlayerCustomAvatarId: (value) => set(() => ({ playerCustomAvatarId: value })),
  setNpcConfigs: (npcs) =>
    set(() => ({
      npcConfigs: npcs.reduce<Record<string, NpcConfig>>((accumulator, npc) => {
        accumulator[npc.id] = npc;
        return accumulator;
      }, {}),
    })),
  setRemotePlayers: (players) =>
    set(() => ({
      remotePlayers: players.reduce<Record<string, RemotePlayerPresence>>((accumulator, player) => {
        accumulator[player.uid] = player;
        return accumulator;
      }, {}),
    })),
  setFriendIds: (friendIds) => set(() => ({ friendIds })),
  setActiveRailTab: (activeRailTab) => set(() => ({ activeRailTab })),
  setRailCollapsed: (isRailCollapsed) => {
    persistRailCollapsed(isRailCollapsed);
    set(() => ({ isRailCollapsed }));
  },
  toggleRailCollapsed: () => {
    const nextValue = !get().isRailCollapsed;
    persistRailCollapsed(nextValue);
    set(() => ({ isRailCollapsed: nextValue }));
  },
}));
